/**
 * TinyMCE Loader - Unified wrapper for TinyMCE editor integration
 *
 * Provides a consistent API for initializing and managing TinyMCE editors
 * throughout the WPS Pages application.
 */

import tinymce from 'tinymce/tinymce';

// Import TinyMCE theme and plugins
import 'tinymce/themes/silver';
import 'tinymce/icons/default';

// Import essential plugins (paste is built-in in TinyMCE 8+)
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/code';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/image';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/media';
import 'tinymce/plugins/nonbreaking';
import 'tinymce/plugins/pagebreak';
import 'tinymce/plugins/quickbars';
import 'tinymce/plugins/save';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/table';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/visualchars';

// Import custom plugins
import { DropZonePlugin } from '../plugins/DropZonePlugin';
import { TemplateClickPlugin } from '../plugins/TemplateClickPlugin';
import { MediaLibraryPlugin } from '../plugins/MediaLibraryPlugin';
import { FontAwesomeIconPickerPlugin } from '../plugins/FontAwesomeIconPickerPlugin';
import { InsertFormPlugin } from '../plugins/InsertFormPlugin';
import { InsertSectionPlugin } from '../plugins/InsertSectionPlugin';
import { ShortcodePreviewPlugin } from '../plugins/ShortcodePreviewPlugin';
import { TabsPlugin } from '../plugins/TabsPlugin';

class TinyMCELoader {
    constructor() {
        this.instances = new Map();
        this.initializing = new Set(); // Synchronous in-flight guard for init()
        this.registrations = new Map();
        this.variablesRegistry = new Map(); // Store variables update functions
        this.defaultConfig = this.getDefaultConfig();

        console.log('📝 TinyMCE Loader initialized');
    }

    /**
     * Get default TinyMCE configuration
     * Matches the features of the previous custom editor
     */
    getDefaultConfig() {
        return {
            // Configure paths for bundled TinyMCE
            base_url: '/wps_pages',
            suffix: '.min',
            skin_url: '/wps_pages/skins/ui/oxide',

            // Disable premium features (open source version)
            license_key: 'gpl',

            // === HTML PRESERVATION (CRITICAL) ===
            // Allow ALL HTML elements and attributes (SVG, custom elements, etc.)
            extended_valid_elements: '*[*]',
            valid_elements: '*[*]',
            valid_children: '+body[style],+div[*],+section[*],+article[*]',
            custom_elements: '~*',

            // Protect template variables and Alpine.js syntax
            protect: [
                /\{\{.*?\}\}/g,        // {{variable}}
                /x-data=".*?"/g,       // Alpine.js x-data
                /x-if=".*?"/g,         // Alpine.js x-if
                /x-for=".*?"/g,        // Alpine.js x-for
                /x-show=".*?"/g,       // Alpine.js x-show
                /@click=".*?"/g,       // Alpine.js @click
                // All Alpine.js directives — only as an attribute (whitespace + @name="..."),
                // so an "@" inside a URL (e.g. tiktok.com/@handle) isn't swallowed
                /(?<=\s)@[\w.:-]+="[^"]*"/g
            ],

            // Don't modify HTML
            verify_html: false,
            cleanup: false,
            convert_urls: false,
            entity_encoding: 'raw',
            entities: '160,nbsp',
            fix_list_elements: false,
            fix_table_elements: false,

            // Paste settings - preserve all attributes
            paste_retain_style_properties: 'all',
            paste_merge_formats: false,
            paste_remove_styles_if_webkit: false,

            // UI configuration
            skin: 'oxide',
            content_css: [
                '/wps_pages/tailwind.css',  // Tailwind CSS (294KB, copied from app/assets/builds)
                '/wps_pages/alpineblocks.css',  // AlpineBlocks frontend styles
                '/admin/pages/pages/page_styles.css'  // Combined page-level classes (engine + host app pages.css)
            ],
            menubar: 'edit view insert format tools table',
            branding: false,
            promotion: false,
            statusbar: true,
            resize: true,
            height: '100%',
            min_height: 600,
            plugins: [
                'advlist',
                'charmap',
                'code',
                'emoticons',
                'fullscreen',
                'image',
                'link',
                'lists',
                'media',
                'nonbreaking',
                'pagebreak',
                'quickbars',
                'save',
                'searchreplace',
                'table',
                'visualblocks',
                'visualchars'
            ],
            // Must be a STRING, not an array — TinyMCE treats an array as multiple
            // toolbar rows and silently ignores toolbar_mode, so the toolbar wraps.
            toolbar:
                'undo redo | blocks medialibrary insertsection insertform | headings | ' +
                'bold italic underline strikethrough | align numlist bullist | ' +
                'link | table media hr | lineheight outdent indent | ' +
                'forecolor backcolor removeformat | charmap emoticons iconpicker | code',
            // Keep the toolbar to a single row; anything that doesn't fit moves
            // into the overflow "..." menu instead of wrapping onto a new line.
            toolbar_mode: 'floating',
            block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6; Blockquote=blockquote',
            fontsize_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',
            font_formats: 'Arial=arial,helvetica,sans-serif; Courier New=courier new,courier,monospace; Georgia=georgia,palatino; Tahoma=tahoma,arial,helvetica,sans-serif; Times New Roman=times new roman,times; Verdana=verdana,geneva',
            image_advtab: true,
            image_title: true,
            // TinyMCE 6.8+ defaults sandbox_iframes:true, which adds a bare
            // sandbox="" attribute to every iframe — that blocks scripts and
            // breaks legitimate embeds like Google Maps and YouTube (the frame
            // renders blank with a "Blocked script execution... sandboxed" error).
            // Exclude trusted embed hosts so their iframes keep working.
            sandbox_iframes_exclusions: [
                'www.google.com',
                'maps.google.com',
                'www.youtube.com',
                'youtube.com',
                'player.vimeo.com'
            ],
            // Wire the standard Insert/Edit Image dialog's Source field to our media
            // library: defining file_picker_callback makes TinyMCE render a "browse"
            // button next to the Source input. It reuses the existing framework-agnostic
            // picker (MediaLibraryPicker.js), which returns the stable public_url
            // (/media/<uuid>, full-resolution). convert_urls:false keeps it verbatim.
            file_picker_types: 'image',
            file_picker_callback: (callback, value, meta) => {
                if (meta.filetype !== 'image') return;
                window.openMediaLibraryPicker({
                    mediaType: 'image',
                    onSelect: ({ url, title }) => {
                        callback(url, { alt: title || '' });
                    }
                });
            },
            image_class_list: [
                { title: 'None', value: '' },
                { title: 'Rounded (slight - 4px)', value: 'rounded' },
                { title: 'Rounded MD (6px)', value: 'rounded-md' },
                { title: 'Rounded LG (8px)', value: 'rounded-lg' },
                { title: 'Rounded XL (12px)', value: 'rounded-xl' },
                { title: 'Rounded 2XL (16px)', value: 'rounded-2xl' },
                { title: 'Rounded 3XL (24px)', value: 'rounded-3xl' },
                { title: 'Full / Circle (50%)', value: 'rounded-full' },
            ],

            content_style: `
                @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');
                /* Reset the layout that leaks into the iframe from tailwind.css (loaded via
                   content_css): Tailwind's base sets
                     body { display: grid; grid-template-rows: auto 1fr auto; min-height: 100dvh }
                   (a sticky-footer layout). Inside the editor the middle 1fr row expands and
                   pushes the last paragraph to the bottom. Force the editable root back to
                   normal block flow. The editable root is <body id="tinymce"
                   class="mce-content-body">; id+class selectors keep this winning even against
                   future !important rules on the body. */
                html, body,
                body#tinymce,
                body.mce-content-body,
                body#tinymce.mce-content-body {
                    display: block !important;
                    flex-direction: initial !important;
                    justify-content: initial !important;
                    align-items: initial !important;
                    height: auto !important;
                    min-height: 100% !important;
                }
                /* Belt-and-braces: never let the editable root distribute its children as
                   flex/grid items (which would make paragraphs grow / drop to the bottom). */
                body#tinymce > *,
                body.mce-content-body > * {
                    flex: none !important;
                }
                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    font-size: 14px;
                    line-height: 1.5;
                    padding: 10px;
                    margin: 0;
                }
                /* Restore normal block-flow spacing that Tailwind Preflight wipes out */
                body p,
                body ul,
                body ol,
                body blockquote,
                body pre {
                    display: block !important;
                    margin: 0 0 1em 0 !important;
                }
                body h1, body h2, body h3, body h4, body h5, body h6 {
                    display: block !important;
                    margin: 1.2em 0 0.6em 0 !important;
                    font-weight: bold !important;
                }
                body h1 { font-size: 2em !important; }
                body h2 { font-size: 1.5em !important; }
                body h3 { font-size: 1.25em !important; }
                body h4 { font-size: 1.1em !important; }
                body ul { list-style: disc outside !important; padding-left: 1.5em !important; }
                body ol { list-style: decimal outside !important; padding-left: 1.5em !important; }
                body li { display: list-item !important; margin: 0.25em 0 !important; }
                /* An empty trailing paragraph should not stretch to the bottom */
                body p:empty { min-height: 1.2em; }
                /* Rounded corner classes (fallback if Tailwind CSS not loaded) */
                img.rounded { border-radius: 0.25rem; }
                img.rounded-md { border-radius: 0.375rem; }
                img.rounded-lg { border-radius: 0.5rem; }
                img.rounded-xl { border-radius: 0.75rem; }
                img.rounded-2xl { border-radius: 1rem; }
                img.rounded-3xl { border-radius: 1.5rem; }
                img.rounded-full { border-radius: 9999px; }
                .variable-token {
                    display: inline-block;
                    background-color: #e3f2fd;
                    padding: 2px 4px;
                    border-radius: 3px;
                    font-family: monospace;
                }
            `,
            setup: (editor) => {
                this.setupEditorCallbacks(editor);
            }
        };
    }

    /**
     * Setup editor callbacks and custom functionality
     */
    setupEditorCallbacks(editor) {
        // Register icons and toolbar buttons during setup (before toolbar renders)
        const iconPicker = new FontAwesomeIconPickerPlugin(editor);
        iconPicker.registerToolbarButton();
        iconPicker.registerMenuItem();

        const insertForm = new InsertFormPlugin(editor);
        insertForm.registerToolbarButton();
        insertForm.registerMenuItem();

        const insertSection = new InsertSectionPlugin(editor);
        insertSection.init();

        editor.on('init', () => {
            console.log(`✅ TinyMCE editor initialized: ${editor.id}`);

            // Override TinyMCE's hard-coded height with 100% and min-height
            const editorContainer = editor.getContainer();
            if (editorContainer) {
                editorContainer.style.height = '100%';
                editorContainer.style.minHeight = '600px';
            }

            // Mirror the live page wrapper + the page's CSS Classes onto the
            // editor body so their effect is visible while editing. The body
            // element is never serialized by getContent(), so it is not saved.
            this.applyPagePreviewWrapper(editor);
            this.bindPagePreviewSync();

            // Initialize template click plugin first (needed by drop zone plugin)
            const templateClick = new TemplateClickPlugin(editor);
            templateClick.init();
            editor.templateClickPlugin = templateClick;

            // Initialize drop zone plugin
            const dropZone = new DropZonePlugin(editor);
            dropZone.init();
            editor.dropZonePlugin = dropZone;

            // Tabs block authoring (needs dropZonePlugin for new tabs' drop zones)
            const tabs = new TabsPlugin(editor);
            tabs.init();
            editor.tabsPlugin = tabs;

            // Render {partial: ...} shortcodes as inline preview blocks
            const shortcodePreview = new ShortcodePreviewPlugin(editor);
            shortcodePreview.init();
            editor.shortcodePreviewPlugin = shortcodePreview;

            // Initialize media library plugin
            const mediaLibrary = new MediaLibraryPlugin(editor);
            mediaLibrary.init();
            editor.mediaLibraryPlugin = mediaLibrary;

            // Complete FontAwesome plugin initialization (CSS injection etc.)
            iconPicker.injectStyles();
            editor.iconPickerPlugin = iconPicker;

            // Store insert form plugin reference
            editor.insertFormPlugin = insertForm;

            // Register global handler for Blocks button (backward compat with old editor)
            if (!window.__richTextHandlers) {
                window.__richTextHandlers = {};
            }
            window.__richTextHandlers[editor.id] = (command) => {
                if (command === 'toggleBlocksSidebar') {
                    this.toggleBlocksSidebar(editor.id);
                } else if (command === 'ensureBlocksSidebarOpen') {
                    this.ensureBlocksSidebarOpen(editor.id);
                }
            };
        });

        editor.on('change', () => {
            editor.save(); // Sync content back to textarea
        });

        // Re-encode {{handlebars}} variables whenever content is loaded into the editor.
        // TinyMCE's code view returns decoded {{ }} via getContent(), and when setContent()
        // is called with raw {{ }}, the protect pattern wraps them in invisible HTML comments.
        // By keeping them as &#123;&#123; the HTML parser just renders them as visible text.
        editor.on('BeforeSetContent', (e) => {
            if (e.content && e.content.includes('{{') && !e.content.includes('&#123;&#123;')) {
                e.content = e.content
                    .replace(/\{\{/g, '&#123;&#123;')
                    .replace(/\}\}/g, '&#125;&#125;');
            }
        });

        // Add custom blocks button to toolbar
        this.addBlocksButton(editor);
        this.addHeadingsButton(editor);
    }

    /**
     * Heading/block-format dropdown. TinyMCE's own "blocks" toolbar item can't be
     * used here — that name is taken by our Blocks sidebar button above — so we
     * register an equivalent menu button under a different name.
     */
    addHeadingsButton(editor) {
        const formats = [
            { title: 'Paragraph', format: 'p' },
            { title: 'Heading 1', format: 'h1' },
            { title: 'Heading 2', format: 'h2' },
            { title: 'Heading 3', format: 'h3' },
            { title: 'Heading 4', format: 'h4' },
            { title: 'Heading 5', format: 'h5' },
            { title: 'Heading 6', format: 'h6' },
            { title: 'Blockquote', format: 'blockquote' }
        ];

        const currentTitle = () => {
            const match = formats.find(f => editor.formatter.match(f.format));
            return match ? match.title : 'Paragraph';
        };

        editor.ui.registry.addMenuButton('headings', {
            text: 'Paragraph',
            tooltip: 'Heading',
            fetch: (callback) => {
                callback(formats.map(f => ({
                    type: 'menuitem',
                    text: f.title,
                    onAction: () => editor.execCommand('FormatBlock', false, f.format)
                })));
            },
            onSetup: (api) => {
                const update = () => api.setText(currentTitle());
                update();
                editor.on('NodeChange', update);
                return () => editor.off('NodeChange', update);
            }
        });
    }

    /**
     * Add Blocks button to toggle AlpineBlocks sidebar
     * Matches the style and functionality of the old custom editor
     */
    addBlocksButton(editor) {
        editor.ui.registry.addIcon('blocksicon', '<svg width="24" height="24" viewBox="0 0 24 24"><path d="M4 3h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm1 2v4h4V5H5Zm9-2h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm1 2v4h4V5h-4ZM4 13h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1Zm1 2v4h4v-4H5Zm9-2h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1Zm1 2v4h4v-4h-4Z" fill-rule="evenodd"/></svg>');

        editor.ui.registry.addButton('blocks', {
            icon: 'blocksicon',
            text: 'Blocks',
            tooltip: 'Blocks',
            onAction: () => {
                // Toggle the sidebar
                this.toggleBlocksSidebar(editor.id);
            }
        });

        this.injectToolbarAccentStyles();
        this.injectDialogBodyStyles();
    }

    /**
     * The skin sizes a dialog's scrolling body as `min(650px, 100vh - 110px)`,
     * which only allows for the header — so the last field scrolls to a stop
     * underneath the footer. Shrink the scroll area to leave room for the
     * footer, and pad the form inside it so the final field clears the
     * footer entirely. The padding goes on `.tox-form` (the scrolled content)
     * rather than the scroll container: browsers don't reliably honour
     * padding-bottom on an overflow container, and the icon-picker plugin
     * resets `.tox-dialog__body-content` padding with `!important` anyway.
     */
    injectDialogBodyStyles() {
        if (document.getElementById('wps-tinymce-dialog-body')) return;

        const style = document.createElement('style');
        style.id = 'wps-tinymce-dialog-body';
        style.textContent = `
            .tox .tox-dialog__body-content {
                max-height: min(560px, calc(100vh - 220px));
            }
            .tox .tox-dialog__body-content .tox-form {
                padding-bottom: 100px;
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Tint the four "insert something" toolbar buttons (Blocks, Media Library,
     * Insert Section, Insert Form) so they read as a distinct group without
     * shouting over the rest of the toolbar. Matched by aria-label because
     * TinyMCE's registry API gives no hook for a custom button class.
     */
    injectToolbarAccentStyles() {
        if (document.getElementById('wps-tinymce-toolbar-accents')) return;

        const style = document.createElement('style');
        style.id = 'wps-tinymce-toolbar-accents';
        style.textContent = `
            .tox .tox-tbtn[aria-label="Blocks"],
            .tox .tox-tbtn[aria-label="Media Library"],
            .tox .tox-tbtn[aria-label="Insert Section"],
            .tox .tox-tbtn[aria-label="Insert Form"] {
                background-color: #eef2ff;
                color: #4338ca;
                border: 1px solid #c7d2fe;
                border-radius: 5px;
            }
            .tox .tox-tbtn[aria-label="Blocks"] svg,
            .tox .tox-tbtn[aria-label="Media Library"] svg,
            .tox .tox-tbtn[aria-label="Insert Section"] svg,
            .tox .tox-tbtn[aria-label="Insert Form"] svg {
                fill: #4338ca;
            }
            .tox .tox-tbtn[aria-label="Blocks"]:hover,
            .tox .tox-tbtn[aria-label="Media Library"]:hover,
            .tox .tox-tbtn[aria-label="Insert Section"]:hover,
            .tox .tox-tbtn[aria-label="Insert Form"]:hover {
                background-color: #e0e7ff;
                border-color: #a5b4fc;
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Apply only the page's user-entered CSS Classes to the editor body as a
     * non-persisted live preview. No base/wrapper classes are added — just what
     * the user typed into the CSS Classes field. No-op outside the page editor
     * (when the #page_css_classes field is absent, e.g. the mailer editor).
     * @param {object} editor - TinyMCE editor instance
     */
    applyPagePreviewWrapper(editor) {
        const input = document.getElementById('page_css_classes');
        if (!input) return;

        const body = editor.getBody && editor.getBody();
        if (!body) return;

        // Remove any previously-applied preview classes before re-applying.
        const prev = body.dataset.pagePreview;
        if (prev) prev.split(/\s+/).forEach(c => c && body.classList.remove(c));

        const applied = (input.value || '').trim();
        applied.split(/\s+/).forEach(c => c && body.classList.add(c));
        body.dataset.pagePreview = applied;
    }

    /**
     * Bind a one-time listener so editing the CSS Classes field updates the
     * preview wrapper on every open TinyMCE editor in real time.
     */
    bindPagePreviewSync() {
        const input = document.getElementById('page_css_classes');
        if (!input || input.dataset.tinyPreviewBound) return;
        input.dataset.tinyPreviewBound = '1';
        input.addEventListener('input', () => {
            if (window.tinymce) {
                window.tinymce.get().forEach(ed => this.applyPagePreviewWrapper(ed));
            }
        });
    }

    /**
     * Toggle the blocks sidebar (show/hide)
     * @param {string} editorId - Editor ID
     */
    toggleBlocksSidebar(editorId) {
        const sidebarId = `blocks-sidebar-${editorId}`;
        const sidebar = document.getElementById(sidebarId);
        const isCurrentlyVisible = sidebar && sidebar.classList.contains('active');

        if (isCurrentlyVisible) {
            // Hide sidebar
            sidebar.classList.remove('active');
            // Remove from DOM after animation completes
            setTimeout(() => {
                if (sidebar && sidebar.parentNode) {
                    sidebar.parentNode.removeChild(sidebar);
                }
            }, 300);
        } else {
            // Open the panel; if a block is currently selected in the editor, jump straight
            // to its Properties tab instead of the default Blocks list.
            this.ensureBlocksSidebarOpen(editorId);
            this.dispatchSelectionIfBlock(editorId);
        }
    }

    /**
     * Idempotently open the blocks sidebar (create + activate if needed).
     * Unlike toggleBlocksSidebar this never closes an already-open panel.
     * @param {string} editorId - Editor ID
     */
    ensureBlocksSidebarOpen(editorId) {
        const sidebarId = `blocks-sidebar-${editorId}`;
        let sidebar = document.getElementById(sidebarId);

        if (sidebar && sidebar.classList.contains('active')) {
            return sidebar; // already open
        }

        if (!sidebar) {
            sidebar = this.createBlocksSidebar(editorId);
            document.body.appendChild(sidebar);

            // Initialize Alpine on the sidebar after appending to DOM
            if (window.Alpine && window.Alpine.initTree) {
                window.Alpine.initTree(sidebar);
            }
        }

        // Show sidebar with animation
        setTimeout(() => {
            sidebar.classList.add('active');
        }, 10);

        return sidebar;
    }

    /**
     * If the editor currently has a block selected, dispatch the same `template-selected`
     * event TemplateClickPlugin uses so the (now open) sidebar switches to its Properties.
     * No-op when the selection is not inside a template block.
     * @param {string} editorId - Editor ID
     */
    dispatchSelectionIfBlock(editorId) {
        const editor = window.tinymce && window.tinymce.get(editorId);
        if (!editor) return;

        const node = editor.selection && editor.selection.getNode();
        const body = editor.getBody && editor.getBody();
        if (!node || !body) return;

        // Walk up to the nearest template wrapper (data-template-id or #template-*).
        let el = node;
        while (el && el !== body) {
            const hasAttr = el.hasAttribute && el.hasAttribute('data-template-id');
            const hasId = el.id && el.id.startsWith('template-');
            if (hasAttr || hasId) {
                const detail = {
                    instanceId: el.id,
                    templateId: el.getAttribute('data-template-id') || 'legacy-template',
                    templateName: el.getAttribute('data-template-name') || 'Template',
                    element: node,
                    clickedElement: node
                };
                // Delay so the freshly-created panel's `template-selected` listener is live.
                setTimeout(() => {
                    document.dispatchEvent(new CustomEvent('template-selected', { detail, bubbles: true }));
                }, 80);
                return;
            }
            el = el.parentNode;
        }
    }

    /**
     * Create the blocks sidebar HTML element
     * @param {string} editorId - Editor ID
     * @returns {HTMLElement} Sidebar element
     */
    createBlocksSidebar(editorId) {
        const sidebar = document.createElement('div');
        sidebar.id = `blocks-sidebar-${editorId}`;
        sidebar.className = 'alpineblocks-sidebar';

        // Initialize Alpine components on the sidebar
        sidebar.setAttribute('x-init', 'init()');
        sidebar.setAttribute('x-data', `{
            activeTab: 'templates',
            templates: [],
            filteredTemplates: [],
            categories: [],
            selectedCategory: 'all',
            searchTerm: '',
            loading: false,
            selectedTemplate: null,
            currentElementStyle: '',
            currentElementTag: '',
            cssProperties: {},
            templateProperties: [],
            templatePropertyValues: {},
            elementClasses: '',
            treeNodes: [],
            activeTreeId: '',
            tabsList: [],
            currentBreakpoint: 'base',
            breakpoints: [
                { key: 'base', label: 'Mobile', min: 0, preview: '390px' },
                { key: 'md', label: 'Tablet', min: 768, preview: '800px' },
                { key: 'lg', label: 'Sm Desk', min: 1024, preview: '1100px' },
                { key: 'xl', label: 'Lg Desk', min: 1280, preview: '100%' }
            ],

            async init() {
                // Register the block-selection listener FIRST — before the async templates
                // load below — so the panel can be opened and immediately select a block
                // (Blocks-button-with-selection and double-click flows depend on this).
                document.addEventListener('template-selected', (e) => {
                    this.selectElement(e.detail.element || e.detail.clickedElement, {
                        templateName: e.detail.templateName,
                        templateId: e.detail.templateId
                    });
                });

                // A block deleted from the canvas context menu clears the panel if it
                // was the thing being edited.
                document.addEventListener('template-deleted', (e) => {
                    const removed = e.detail && e.detail.element;
                    const current = this.selectedTemplate && this.selectedTemplate.element;
                    if (removed && current && (removed === current || removed.contains(current))) {
                        this.selectedTemplate = null;
                        this.tabsList = [];
                    }
                });

                // Initialize templates component
                if (window.editorTemplatesWithCategories) {
                    const templatesData = window.editorTemplatesWithCategories();
                    if (templatesData.init) {
                        await templatesData.init.call(templatesData);
                    }
                    // Copy data to parent scope after async init completes
                    this.templates = templatesData.templates || [];
                    this.filteredTemplates = templatesData.filteredTemplates || [];
                    this.categories = templatesData.categories || [];
                    // Copy methods to parent scope so they're accessible in templates
                    this.handleTemplateDoubleClick = templatesData.handleTemplateDoubleClick?.bind(templatesData);
                    this.handleTemplateDragStart = templatesData.handleTemplateDragStart?.bind(templatesData);
                    this.handleTemplateDragEnd = templatesData.handleTemplateDragEnd?.bind(templatesData);
                    this.categorizeTemplate = templatesData.categorizeTemplate?.bind(templatesData);
                } else if (window.editorTemplates) {
                    const templatesData = window.editorTemplates();
                    if (templatesData.init) {
                        await templatesData.init.call(templatesData);
                    }
                    // Copy data to parent scope after async init completes
                    this.templates = templatesData.templates || [];
                    this.filteredTemplates = templatesData.filteredTemplates || [];
                    this.categories = templatesData.categories || [];
                    // Copy methods to parent scope so they're accessible in templates
                    this.handleTemplateDoubleClick = templatesData.handleTemplateDoubleClick?.bind(templatesData);
                    this.handleTemplateDragStart = templatesData.handleTemplateDragStart?.bind(templatesData);
                    this.handleTemplateDragEnd = templatesData.handleTemplateDragEnd?.bind(templatesData);
                    this.categorizeTemplate = templatesData.categorizeTemplate?.bind(templatesData);
                }

                // The nested cssClassTypeahead component lives inside this sidebar and
                // dispatches a bubbling 'wps-classes-changed' event on edit/select; apply
                // it to the currently selected element here.
                this.$root.addEventListener('wps-classes-changed', (e) => {
                    this.applyElementClasses(e.detail);
                });

                // The device-preview switcher lives in the Content header and owns canvas
                // resizing. Here we only sync which breakpoint the property panel edits.
                this.currentBreakpoint = window.wpsCurrentBreakpoint || 'base';
                document.addEventListener('wps:breakpoint', (e) => {
                    this.currentBreakpoint = (e.detail && e.detail.breakpoint) || 'base';
                    if (this.selectedTemplate) this.loadTemplateProperties(this.selectedTemplate);
                });
            },

            // Single funnel for selecting ANY element (template block click OR tree click).
            selectElement(el, opts) {
                if (!el) return;
                opts = opts || {};
                const wrapper = el.closest('[data-template-instance]') || el.closest('[data-template-id]');
                const isBlock = !!wrapper;
                const tag = (el.tagName || 'div').toLowerCase();
                this.selectedTemplate = {
                    element: el,
                    clickedElement: el,
                    templateName: opts.templateName || (isBlock ? (wrapper.getAttribute('data-template-name') || 'Block') : tag),
                    templateId: opts.templateId || (isBlock ? (wrapper.getAttribute('data-template-id') || '') : ''),
                    instanceId: isBlock ? wrapper.id : '',
                    currentElementStyle: el.getAttribute('style') || ''
                };
                this.currentElementStyle = this.selectedTemplate.currentElementStyle;
                this.currentElementTag = tag;
                this.currentBreakpoint = window.wpsCurrentBreakpoint || 'base';
                this.parseCSSProperties();
                this.loadTemplateProperties(this.selectedTemplate);
                this.loadElementClasses();
                this.highlightSelected(el);
                this.activeTab = 'properties';
            },

            // Class target: the block wrapper for elements inside a block, else the element itself.
            _classTarget() {
                const el = this.selectedTemplate && this.selectedTemplate.element;
                if (!el) return null;
                return el.closest('[data-template-instance]') || el;
            },

            loadElementClasses() {
                const t = this._classTarget();
                const internal = ['mce-content-body', 'mce-edit-focus'];
                const cls = t ? (t.getAttribute('class') || '') : '';
                this.elementClasses = cls.split(/\\s+/).filter(c => c && internal.indexOf(c) === -1).join(' ');
                // Push into the nested typeahead so its input reflects the new selection.
                window.dispatchEvent(new CustomEvent('wps-classes-sync', { detail: this.elementClasses }));
            },

            applyElementClasses(value) {
                const t = this._classTarget();
                if (!t) return;
                this.elementClasses = value;
                const next = (value || '').split(/\\s+/).filter(Boolean);
                // Preserve structural classes the user shouldn't lose by clearing the field.
                const structural = ['template-wrapper', 'wps-col', 'richtext-nested-dropzone'];
                const keep = structural.filter(c => t.classList.contains(c));
                const merged = keep.concat(next.filter(c => keep.indexOf(c) === -1));
                t.setAttribute('class', merged.join(' '));
                const mceEditor = window.tinymce && window.tinymce.get('${editorId}');
                if (mceEditor) mceEditor.dispatch('change');
            },

            buildTree() {
                const ed = window.tinymce && window.tinymce.get('${editorId}');
                const body = ed && ed.getBody();
                if (!body) { this.treeNodes = []; return; }
                const out = [];
                let counter = 0;
                const walk = (el, depth) => {
                    for (let i = 0; i < el.children.length; i++) {
                        const child = el.children[i];
                        const tag = (child.tagName || '').toLowerCase();
                        if (tag === 'style' || tag === 'script' || tag === 'br') continue;
                        const cls = (child.getAttribute('class') || '').split(/\\s+/).filter(Boolean);
                        // Skip transient dropzone/indicator nodes from the listing, but still descend.
                        const isTransient = cls.indexOf('richtext-nested-dropzone') !== -1 || cls.indexOf('richtext-drop-indicator') !== -1;
                        if (!isTransient) {
                            const id = 'wpstree-' + (counter++);
                            child.setAttribute('data-wps-tree-id', id);
                            let label = tag;
                            if (child.id) label += '#' + child.id;
                            else if (cls.length) label += '.' + cls.slice(0, 2).join('.');
                            if (child.hasAttribute('data-template-instance')) label += '  [block]';
                            out.push({ id: id, depth: depth, label: label });
                            walk(child, depth + 1);
                        } else {
                            walk(child, depth);
                        }
                    }
                };
                walk(body, 0);
                this.treeNodes = out;
                const sel = this.selectedTemplate && this.selectedTemplate.element;
                this.activeTreeId = (sel && sel.getAttribute) ? (sel.getAttribute('data-wps-tree-id') || '') : '';
            },

            selectElementByTreeId(id) {
                const ed = window.tinymce && window.tinymce.get('${editorId}');
                const body = ed && ed.getBody();
                const el = body && body.querySelector('[data-wps-tree-id="' + id + '"]');
                if (!el) return;
                this.activeTreeId = id;
                this.selectElement(el);
            },

            highlightSelected(el) {
                const ed = window.tinymce && window.tinymce.get('${editorId}');
                const body = ed && ed.getBody();
                if (!body) return;
                const doc = body.ownerDocument;
                // Inject the highlight stylesheet once into the iframe head (kept out of saved body HTML).
                if (doc && doc.head && !doc.getElementById('wps-highlight-style')) {
                    const styleEl = doc.createElement('style');
                    styleEl.id = 'wps-highlight-style';
                    // Positive outline-offset pushes the border a few px away from the
                    // element. Outlines don't participate in layout, so this breathing
                    // room costs nothing — padding here would shift the block's contents.
                    styleEl.textContent = '[data-wps-highlight]{outline:2px solid #3b82f6 !important; outline-offset:4px;}';
                    doc.head.appendChild(styleEl);
                }
                const prev = body.querySelectorAll('[data-wps-highlight]');
                for (let i = 0; i < prev.length; i++) prev[i].removeAttribute('data-wps-highlight');
                if (el && el.setAttribute) el.setAttribute('data-wps-highlight', '1');
            },

            filterTemplates() {
                let filtered = this.templates;
                if (this.selectedCategory !== 'all') {
                    filtered = filtered.filter(t => {
                        const cat = this.categorizeTemplate ? this.categorizeTemplate(t) : (t.category || 'other');
                        return cat === this.selectedCategory;
                    });
                }
                if (this.searchTerm && this.searchTerm.trim() !== '') {
                    const q = this.searchTerm.toLowerCase().trim();
                    filtered = filtered.filter(t => {
                        return (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q);
                    });
                }
                this.filteredTemplates = filtered;
            },

            parseCSSProperties() {
                const style = this.currentElementStyle || '';
                const props = {};
                style.split(';').forEach(prop => {
                    const colonIndex = prop.indexOf(':');
                    if (colonIndex === -1) return;
                    const key = prop.substring(0, colonIndex).trim();
                    const value = prop.substring(colonIndex + 1).trim();
                    if (key && value) {
                        props[key] = value;
                    }
                });
                this.cssProperties = props;
            },

            updateCSSProperty(property, value) {
                this.cssProperties[property] = value;
                this.applyStyles();
            },

            applyStyles() {
                const styleString = Object.entries(this.cssProperties)
                    .filter(([_, val]) => val)
                    .map(([key, val]) => \`\${key}: \${val}\`)
                    .join('; ');
                if (this.selectedTemplate?.element) {
                    this.currentElementStyle = styleString;
                    this.writeElementStyle(styleString);
                }
            },

            // Inline CSS textarea: apply the text as typed. Round-tripping it
            // through cssProperties (applyStyles) strips a trailing ";" and any
            // half-typed declaration, so new properties could never be added.
            applyInlineStyle() {
                this.parseCSSProperties();
                this.writeElementStyle(this.currentElementStyle);
            },

            writeElementStyle(styleString) {
                const el = this.selectedTemplate?.element;
                if (!el) return;
                el.setAttribute('style', styleString);
                el.removeAttribute('data-mce-style');
                const mceEditor = window.tinymce?.get('${editorId}');
                if (mceEditor) mceEditor.dispatch('change');
            },

            // --- Tabs block ------------------------------------------------
            // Thin delegations; the DOM work lives in plugins/TabsPlugin.js so
            // esbuild can actually check it (this x-data is an opaque string).
            get isTabsBlock() {
                return this.tabsList.length > 0;
            },

            refreshTabs() {
                const el = this.selectedTemplate && this.selectedTemplate.element;
                this.tabsList = (el && window.WpsTabs) ? window.WpsTabs.list(el) : [];
            },

            _tabsEl() {
                return this.selectedTemplate && this.selectedTemplate.element;
            },

            addTab() {
                if (!window.WpsTabs) return;
                window.WpsTabs.add(this._tabsEl());
                this.refreshTabs();
            },

            removeTab(tabId) {
                if (!window.WpsTabs) return;
                window.WpsTabs.remove(this._tabsEl(), tabId);
                this.refreshTabs();
            },

            renameTab(tabId, label) {
                if (!window.WpsTabs) return;
                window.WpsTabs.rename(this._tabsEl(), tabId, label);
                this.refreshTabs();
            },

            moveTab(tabId, delta) {
                if (!window.WpsTabs) return;
                window.WpsTabs.reorder(this._tabsEl(), tabId, delta);
                this.refreshTabs();
            },

            setDefaultTab(tabId) {
                if (!window.WpsTabs) return;
                window.WpsTabs.setDefault(this._tabsEl(), tabId);
                this.refreshTabs();
            },

            previewTab(tabId) {
                if (!window.WpsTabs) return;
                window.WpsTabs.preview(this._tabsEl(), tabId);
                this.refreshTabs();
            },

            loadTemplateProperties(detail) {
                this.templateProperties = [];
                this.templatePropertyValues = {};
                this.refreshTabs();
                const element = detail.element || detail.clickedElement;
                if (!element) return;
                let wrapper = element;
                while (wrapper && !wrapper.hasAttribute('data-template-id') &&
                       !wrapper.hasAttribute('data-template-instance') &&
                       !wrapper.hasAttribute('data-template-properties')) {
                    wrapper = wrapper.parentElement;
                }
                if (!wrapper) return;
                const schema = window.WpsTemplateSchemaResolver?.resolveTemplateSchema(
                    detail.templateId || wrapper.getAttribute('data-template-id'),
                    detail.templateName || wrapper.getAttribute('data-template-name')
                );
                if (schema) this.templateProperties = schema;
                // Legacy fallback: schema embedded on the wrapper (no longer written)
                if (this.templateProperties.length === 0 && wrapper.hasAttribute('data-template-properties')) {
                    try {
                        const props = JSON.parse(wrapper.getAttribute('data-template-properties'));
                        if (Array.isArray(props) && props.length > 0) this.templateProperties = props;
                    } catch (e) { /* ignore */ }
                }
                if (this.templateProperties.length > 0) {
                    let tw = element.closest('[data-template-instance]') || wrapper;
                    this.templateProperties.forEach(prop => {
                        let val = prop.default || '';
                        try {
                            if (prop.type === 'layout') {
                                this.templatePropertyValues[prop.name] = this.readLayoutValue(tw, prop);
                                return;
                            }
                            if (prop.responsive && prop.attribute === 'style' && prop.property) {
                                this.templatePropertyValues[prop.name] = this.readResponsiveValue(tw, prop, this.currentBreakpoint);
                                return;
                            }
                            const tgt = tw.querySelector(prop.selector);
                            if (tgt) {
                                if (prop.attribute === 'textContent' || !prop.attribute) val = tgt.textContent || prop.default || '';
                                else if (prop.attribute === 'src') val = tgt.getAttribute('src') || prop.default || '';
                                else if (prop.attribute === 'style' && prop.property) {
                                    // Support property as string or array - read from first one
                                    const readProp = Array.isArray(prop.property) ? prop.property[0] : prop.property;
                                    val = tgt.style.getPropertyValue(readProp);
                                    if (!val) val = getComputedStyle(tgt).getPropertyValue(readProp);
                                    if (!val) val = prop.default || '';
                                    // Convert rgb() to hex for color inputs
                                    if (prop.type === 'color') {
                                        const m = String(val).match(/rgb\\((\\d+),\\s*(\\d+),\\s*(\\d+)\\)/);
                                        if (m) val = '#' + [m[1],m[2],m[3]].map(x => parseInt(x).toString(16).padStart(2,'0')).join('');
                                    }
                                    // Strip units for range inputs so slider gets numeric value
                                    if (prop.type === 'range') {
                                        const n = parseFloat(val);
                                        if (!isNaN(n)) val = n;
                                    }
                                }
                                else val = tgt.getAttribute(prop.attribute) || prop.default || '';
                            }
                        } catch (e) { /* use default */ }
                        this.templatePropertyValues[prop.name] = val;
                    });
                }
            },

            updateTemplateProperty(propName, value) {
                this.templatePropertyValues[propName] = value;
                const prop = this.templateProperties.find(p => p.name === propName);
                if (!prop || !this.selectedTemplate) return;
                const element = this.selectedTemplate.element || this.selectedTemplate.clickedElement;
                if (!element) return;
                let wrapper = element.closest('[data-template-instance]') || element.closest('[data-template-id]');
                if (!wrapper) return;
                const target = wrapper.querySelector(prop.selector);
                if (!target) return;
                if (prop.type === 'layout') {
                    this.applyLayout(prop, value, wrapper, target);
                    this._dispatchChange();
                    return;
                }
                if (prop.responsive && prop.attribute === 'style' && prop.property) {
                    this.applyResponsiveStyle(wrapper, target, prop, value);
                    this._dispatchChange();
                    return;
                }
                if (prop.attribute === 'textContent' || !prop.attribute) target.textContent = value;
                else if (prop.attribute === 'src') {
                    target.setAttribute('src', value);
                    // TinyMCE serializes from data-mce-src, not src
                    target.removeAttribute('data-mce-src');
                }
                else if (prop.attribute === 'style' && prop.property) {
                    let cssValue = value;
                    if (prop.type === 'range' && prop.default) {
                        const unitMatch = String(prop.default).match(/[a-z%]+$/i);
                        if (unitMatch) cssValue = value + unitMatch[0];
                    }
                    let propList = prop.property;
                    if (typeof propList === 'string' && propList.trim().startsWith('[')) {
                        try { propList = JSON.parse(propList); } catch(e) {}
                    }
                    const props = Array.isArray(propList) ? propList : [propList];
                    props.forEach(p => target.style.setProperty(p, cssValue));
                    target.removeAttribute('data-mce-style');
                }
                else if (prop.attribute === 'class' && prop.classPattern) {
                    const classes = target.className.split(' ').filter(c => !c.startsWith(prop.classPattern));
                    classes.push(value);
                    target.className = classes.join(' ');
                } else {
                    target.setAttribute(prop.attribute, value);
                    // TinyMCE serializes href (and other URL attrs) from its data-mce-* cache
                    target.removeAttribute('data-mce-' + prop.attribute);
                }
                const mceEditor = window.tinymce?.get('${editorId}');
                if (mceEditor) mceEditor.dispatch('change');
            },

            _dispatchChange() {
                const ed = window.tinymce && window.tinymce.get('${editorId}');
                if (ed) ed.dispatch('change');
            },

            _bpMin(key) {
                const b = this.breakpoints.find(x => x.key === key);
                return b ? b.min : 0;
            },

            _propList(prop) {
                let propList = prop.property;
                if (typeof propList === 'string' && propList.trim().charAt(0) === '[') {
                    try { propList = JSON.parse(propList); } catch (e) {}
                }
                return Array.isArray(propList) ? propList : [propList];
            },

            _instanceId(wrapper) {
                return wrapper.getAttribute('data-template-instance') || wrapper.getAttribute('data-template-id') || '';
            },

            _findStyleEl(wrapper) {
                const kids = wrapper.children;
                for (let i = 0; i < kids.length; i++) {
                    if (kids[i].tagName === 'STYLE' && kids[i].hasAttribute('data-instance-style')) return kids[i];
                }
                return null;
            },

            _getStyleEl(wrapper) {
                let styleEl = this._findStyleEl(wrapper);
                if (!styleEl) {
                    styleEl = wrapper.ownerDocument.createElement('style');
                    styleEl.setAttribute('data-instance-style', this._instanceId(wrapper));
                    wrapper.insertBefore(styleEl, wrapper.firstChild);
                }
                return styleEl;
            },

            _readRules(styleEl) {
                try { return JSON.parse(styleEl.getAttribute('data-rules') || '{}'); } catch (e) { return {}; }
            },

            _writeRules(styleEl, rules, instanceId) {
                styleEl.setAttribute('data-rules', JSON.stringify(rules));
                const order = ['base', 'md', 'lg', 'xl'];
                const sel = '[data-template-instance="' + instanceId + '"] ';
                let css = '';
                for (const ruleKey in rules) {
                    const sep = ruleKey.indexOf('||');
                    if (sep === -1) continue;
                    const selector = ruleKey.substring(0, sep);
                    const property = ruleKey.substring(sep + 2);
                    const vals = rules[ruleKey] || {};
                    for (let i = 0; i < order.length; i++) {
                        const bp = order[i];
                        const v = vals[bp];
                        if (v === undefined || v === null || v === '') continue;
                        const decl = sel + selector + ' { ' + property + ': ' + v + ' !important; }';
                        if (bp === 'base') css += decl + '\\n';
                        else css += '@media (min-width: ' + this._bpMin(bp) + 'px) { ' + decl + ' }\\n';
                    }
                }
                styleEl.textContent = css;
            },

            applyResponsiveStyle(wrapper, target, prop, value) {
                const instanceId = this._instanceId(wrapper);
                const styleEl = this._getStyleEl(wrapper);
                const rules = this._readRules(styleEl);
                let cssValue = value;
                if (prop.type === 'range' && prop.default) {
                    const unitMatch = String(prop.default).match(/[a-z%]+$/i);
                    if (unitMatch && String(value).match(/^[\\d.\\-]+$/)) cssValue = value + unitMatch[0];
                }
                const props = this._propList(prop);
                for (let i = 0; i < props.length; i++) {
                    const p = props[i];
                    const key = prop.selector + '||' + p;
                    if (!rules[key]) rules[key] = {};
                    rules[key][this.currentBreakpoint] = cssValue;
                    // Seed the base value so a single edit (at any device) applies across all
                    // widths; larger breakpoints then layer on as explicit overrides.
                    if (this.currentBreakpoint !== 'base' && (rules[key]['base'] === undefined || rules[key]['base'] === '')) {
                        rules[key]['base'] = cssValue;
                    }
                    try { target.style.removeProperty(p); } catch (e) {}
                }
                target.removeAttribute('data-mce-style');
                this._writeRules(styleEl, rules, instanceId);
            },

            readResponsiveValue(wrapper, prop, bp) {
                const styleEl = this._findStyleEl(wrapper);
                let raw = '';
                if (styleEl) {
                    const rules = this._readRules(styleEl);
                    const key = prop.selector + '||' + this._propList(prop)[0];
                    const vals = rules[key] || {};
                    raw = (vals[bp] !== undefined && vals[bp] !== '') ? vals[bp] : (vals['base'] || '');
                }
                if (!raw) {
                    const tgt = wrapper.querySelector(prop.selector);
                    if (tgt) {
                        const p = this._propList(prop)[0];
                        const win = wrapper.ownerDocument.defaultView || window;
                        raw = tgt.style.getPropertyValue(p) || win.getComputedStyle(tgt).getPropertyValue(p) || prop.default || '';
                    } else {
                        raw = prop.default || '';
                    }
                }
                if (prop.type === 'range') {
                    const n = parseFloat(raw);
                    return isNaN(n) ? '' : n;
                }
                return raw;
            },

            layoutPresets() {
                return [
                    { id: '1', label: '1 column', cols: 1, template: '1fr' },
                    { id: '1-1', label: '1/2 + 1/2', cols: 2, template: '1fr 1fr' },
                    { id: '1-2', label: '1/3 + 2/3', cols: 2, template: '1fr 2fr' },
                    { id: '2-1', label: '2/3 + 1/3', cols: 2, template: '2fr 1fr' },
                    { id: '1-1-1', label: '1/3 x 3', cols: 3, template: '1fr 1fr 1fr' },
                    { id: '1-1-1-1', label: '1/4 x 4', cols: 4, template: '1fr 1fr 1fr 1fr' }
                ];
            },

            _cols(row) {
                return Array.prototype.filter.call(row.children, c => c.classList && c.classList.contains('wps-col'));
            },

            _makeDropzone(doc, parentId) {
                const dz = doc.createElement('div');
                dz.className = 'richtext-nested-dropzone';
                dz.setAttribute('data-zone-id', parentId + '-dz-' + Math.floor(Math.random() * 1e9));
                dz.setAttribute('data-parent-id', parentId);
                dz.setAttribute('contenteditable', 'false');
                dz.setAttribute('style', 'min-height: 60px; border: 2px dashed #d1d5db; border-radius: 8px; margin: 8px 0; padding: 16px; text-align: center; color: #9ca3af; background: #f9fafb; cursor: pointer;');
                // Placeholder text MUST match DropZonePlugin.cleanupForSave's empty-check so
                // empty trailing dropzones collapse back to <!-- drop --> comments on save.
                dz.innerHTML = '<span style="font-size: 13px; font-weight: 500; pointer-events: none;">Drop template here</span>';
                return dz;
            },

            _makeColumn(doc, parentId) {
                const col = doc.createElement('div');
                col.className = 'wps-col';
                col.appendChild(this._makeDropzone(doc, parentId));
                return col;
            },

            applyLayout(prop, presetId, wrapper, row) {
                const preset = this.layoutPresets().find(p => p.id === presetId);
                if (!preset || !row) return;
                const doc = row.ownerDocument;
                const instanceId = this._instanceId(wrapper);
                // 1. Responsive grid-template-columns (+ base display/gap defaults)
                const styleEl = this._getStyleEl(wrapper);
                const rules = this._readRules(styleEl);
                const colKey = prop.selector + '||grid-template-columns';
                if (!rules[colKey]) rules[colKey] = {};
                rules[colKey][this.currentBreakpoint] = preset.template;
                // Apply the chosen layout across all widths by default; users can override a
                // specific device (e.g. set Mobile to "1 column") to stack on smaller screens.
                if (rules[colKey]['base'] === undefined || rules[colKey]['base'] === '') rules[colKey]['base'] = preset.template;
                const dispKey = prop.selector + '||display';
                if (!rules[dispKey]) rules[dispKey] = {};
                if (!rules[dispKey]['base']) rules[dispKey]['base'] = 'grid';
                const gapKey = prop.selector + '||gap';
                if (!rules[gapKey]) rules[gapKey] = {};
                if (!rules[gapKey]['base']) rules[gapKey]['base'] = '24px';
                this._writeRules(styleEl, rules, instanceId);
                try { row.style.removeProperty('grid-template-columns'); } catch (e) {}
                // 2. Reconcile column count (preserve dropped content when shrinking)
                let cols = this._cols(row);
                if (preset.cols > cols.length) {
                    for (let i = cols.length; i < preset.cols; i++) row.appendChild(this._makeColumn(doc, instanceId));
                } else if (preset.cols < cols.length) {
                    const keeper = cols[preset.cols - 1];
                    for (let i = preset.cols; i < cols.length; i++) {
                        const surplus = cols[i];
                        const moved = Array.prototype.filter.call(surplus.children, c => c.hasAttribute && c.hasAttribute('data-template-instance'));
                        moved.forEach(node => {
                            const dz = keeper.querySelector(':scope > .richtext-nested-dropzone');
                            if (dz) keeper.insertBefore(node, dz); else keeper.appendChild(node);
                        });
                        surplus.remove();
                    }
                }
                // 3. Ensure every column keeps a trailing dropzone
                cols = this._cols(row);
                cols.forEach(col => {
                    if (!col.querySelector(':scope > .richtext-nested-dropzone')) col.appendChild(this._makeDropzone(doc, instanceId));
                });
            },

            readLayoutValue(wrapper, prop) {
                const styleEl = this._findStyleEl(wrapper);
                let tmpl = '';
                if (styleEl) {
                    const rules = this._readRules(styleEl);
                    const vals = rules[prop.selector + '||grid-template-columns'] || {};
                    tmpl = (vals[this.currentBreakpoint] && vals[this.currentBreakpoint] !== '') ? vals[this.currentBreakpoint] : (vals['base'] || '');
                }
                if (!tmpl) {
                    const row = wrapper.querySelector(prop.selector);
                    if (row) tmpl = row.style.getPropertyValue('grid-template-columns') || '';
                }
                const preset = this.layoutPresets().find(p => p.template === String(tmpl).trim());
                return preset ? preset.id : '';
            }
        }`);

        sidebar.innerHTML = `
            <style>
                .alpineblocks-sidebar {
                    position: fixed;
                    top: 0;
                    left: -280px;
                    width: 280px;
                    height: 100vh;
                    background: white;
                    border-right: 1px solid #e5e7eb;
                    z-index: 9999;
                    display: flex;
                    flex-direction: column;
                    transition: left 0.3s ease-in-out;
                    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
                }

                .alpineblocks-sidebar.active {
                    left: 0;
                }

                .alpineblocks-sidebar .panel-header {
                    padding: 1rem;
                    border-bottom: 1px solid #e5e7eb;
                    font-weight: 600;
                    font-size: 0.875rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .alpineblocks-sidebar .panel-tabs {
                    display: flex;
                    border-bottom: 1px solid #e5e7eb;
                    background: #f9fafb;
                }

                .alpineblocks-sidebar .panel-tab {
                    flex: 1;
                    padding: 0.75rem;
                    border: none;
                    background: transparent;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    color: #6b7280;
                    border-bottom: 2px solid transparent;
                }

                .alpineblocks-sidebar .panel-tab:hover {
                    color: #2563eb;
                }

                .alpineblocks-sidebar .panel-tab.active {
                    color: #2563eb;
                    border-bottom-color: #2563eb;
                    background: white;
                }

                .alpineblocks-sidebar .panel-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 1rem;
                }

                .alpineblocks-sidebar .close-btn {
                    margin-left: auto;
                    background: transparent;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 0.25rem;
                    border-radius: 0.25rem;
                    transition: all 0.2s ease;
                }

                .alpineblocks-sidebar .close-btn:hover {
                    background: #e5e7eb;
                    color: #111827;
                }
            </style>

            <div class="panel-header">
                <svg class="size-4" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
                </svg>
                Blocks
                <button class="close-btn" onclick="window.__richTextHandlers && window.__richTextHandlers['${editorId}'] && window.__richTextHandlers['${editorId}']('toggleBlocksSidebar')" type="button">
                    <svg style="width: 1.25rem; height: 1.25rem;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>

            <div class="panel-tabs">
                <button class="panel-tab"
                        :class="{ 'active': activeTab === 'templates' }"
                        @click="activeTab = 'templates'"
                        title="Blocks"
                        type="button">
                    <svg style="width: 20px; height: 20px; margin: 0 auto; display: block;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z"></path>
                    </svg>
                </button>
                <button class="panel-tab"
                        :class="{ 'active': activeTab === 'properties' }"
                        @click="activeTab = 'properties'"
                        title="Properties"
                        type="button">
                    <svg style="width: 20px; height: 20px; margin: 0 auto; display: block;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
                    </svg>
                </button>
                <button class="panel-tab"
                        :class="{ 'active': activeTab === 'tree' }"
                        @click="activeTab = 'tree'; buildTree()"
                        title="Tree"
                        type="button">
                    <svg style="width: 20px; height: 20px; margin: 0 auto; display: block;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 5h6M3 12h12M3 19h8M15 5h6M17 12h4M13 19h8"></path>
                    </svg>
                </button>
            </div>

            <div class="panel-content">
                <!-- Templates Tab -->
                <div x-show="activeTab === 'templates'">
                    <div class="templates-section">
                        <div class="templates-header" style="margin-bottom: 1rem;">
                            <!-- Search -->
                            <div style="margin-bottom: 0.75rem;">
                                <input type="text"
                                       placeholder="Search blocks..."
                                       style="width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #d1d5db; border-radius: 0.5rem; font-size: 0.875rem;"
                                       x-model="searchTerm"
                                       @input="filterTemplates && filterTemplates()">
                            </div>
                            <!-- Category Filter -->
                            <div style="margin-bottom: 1rem;">
                                <select style="width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #d1d5db; border-radius: 0.5rem; font-size: 0.875rem;"
                                        x-model="selectedCategory"
                                        @change="filterTemplates && filterTemplates()">
                                    <option value="all">All Blocks</option>
                                    <template x-for="cat in categories" :key="cat.id">
                                        <option :value="cat.id" x-text="cat.name"></option>
                                    </template>
                                </select>
                            </div>
                            <!-- Usage Help -->
                            <div style="padding: 0.75rem; background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 0.375rem; margin-bottom: 1rem;">
                                <div style="font-size: 0.75rem; line-height: 1.4; color: #0c4a6e;">
                                    <strong>Quick Tip:</strong> Double-click a block to insert at cursor position
                                </div>
                            </div>
                        </div>
                        <div class="templates-grid">
                            <template x-for="template in filteredTemplates" :key="template.id">
                                <div class="template-item"
                                     draggable="true"
                                     @dragstart="handleTemplateDragStart && handleTemplateDragStart($event, template)"
                                     @dragend="handleTemplateDragEnd && handleTemplateDragEnd($event)"
                                     @dblclick="handleTemplateDoubleClick && handleTemplateDoubleClick($event, template)"
                                     style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; border: 1px solid #e5e7eb; border-radius: 0.375rem; background: white; margin-bottom: 0.5rem; transition: all 0.2s ease; cursor: pointer;"
                                     onmouseover="this.style.borderColor='#93c5fd'; this.style.background='#eff6ff'; this.style.transform='translateY(-1px)'"
                                     onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'; this.style.transform='translateY(0)'">
                                    <div class="template-preview" style="flex: 1;">
                                        <template x-if="template.screenshot_url">
                                            <div>
                                                <div class="template-name" x-text="template.name" style="font-size: 0.875rem; font-weight: 500; color: #374151; margin-bottom: 0.375rem;"></div>
                                                <img :src="template.screenshot_url" :alt="template.name" style="width: 100%; border-radius: 0.25rem; border: 1px solid #e5e7eb;" loading="lazy">
                                            </div>
                                        </template>
                                        <template x-if="!template.screenshot_url">
                                            <div>
                                                <div class="template-header" style="display: flex; align-items: center; gap: 0.5rem;">
                                                    <div class="template-icon" x-html="template.icon" style="font-size: 1.125rem; min-width: 20px;"></div>
                                                    <div class="template-name" x-text="template.name" style="font-size: 0.875rem; font-weight: 500; color: #374151;"></div>
                                                </div>
                                                <div class="template-description" x-text="template.description" style="font-size: 0.75rem; color: #6b7280; margin-top: 0.25rem;"></div>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div x-show="filteredTemplates.length === 0" style="text-align: center; padding: 2rem 0; color: #9ca3af;">
                            <p x-show="loading" style="font-size: 0.875rem;">Loading blocks...</p>
                            <p x-show="!loading && templates.length === 0" style="font-size: 0.875rem;">No blocks found.</p>
                            <p x-show="!loading && templates.length > 0 && filteredTemplates.length === 0" style="font-size: 0.875rem;">No blocks match your filters.</p>
                        </div>
                    </div>
                </div>

                <!-- Properties Tab -->
                <div x-show="activeTab === 'properties'">
                    <!-- Empty State -->
                    <div x-show="!selectedTemplate" style="text-align: center; padding: 3rem 1rem; color: #9ca3af;">
                        <svg style="width: 48px; height: 48px; margin: 0 auto 1rem; color: #d1d5db;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
                        </svg>
                        <p style="font-size: 0.875rem; font-weight: 500; color: #6b7280; margin-bottom: 0.5rem;">No block selected</p>
                        <p style="font-size: 0.75rem; color: #9ca3af;">Click on a block in the editor to view its properties</p>
                    </div>

                    <!-- Properties Editor -->
                    <div x-show="selectedTemplate" style="padding: 0;">
                        <div style="padding: 0.75rem; background: #f9fafb; border-bottom: 1px solid #e5e7eb; margin-bottom: 1rem;">
                            <div style="font-size: 0.7rem; font-weight: 600; color: #6b7280; margin-bottom: 0.5rem;">BLOCK INFO</div>
                            <div style="font-size: 0.75rem; color: #374151;">
                                <div><strong>Name:</strong> <span x-text="selectedTemplate?.templateName || 'Unknown'"></span></div>
                                <div><strong>ID:</strong> <code style="font-family: monospace; font-size: 0.7rem;" x-text="selectedTemplate?.templateId || 'unknown'"></code></div>
                            </div>
                        </div>

                        <!-- Tabs block: add / rename / reorder / remove tabs -->
                        <div x-show="isTabsBlock" style="margin-bottom: 1rem; padding: 0.75rem; border: 1px solid #e5e7eb; border-radius: 0.5rem;">
                            <div style="font-size: 0.7rem; font-weight: 600; color: #6b7280; margin-bottom: 0.5rem;">TABS</div>
                            <div style="font-size: 0.65rem; color: #6b7280; margin-bottom: 0.5rem;">Click a tab to edit its content. The starred tab is the one visitors see first.</div>

                            <template x-for="(tab, i) in tabsList" :key="tab.id">
                                <div style="display: flex; flex-wrap: nowrap; align-items: center; gap: 2px; margin-bottom: 4px;"
                                     :style="tab.isPreviewing ? 'background:#eff6ff; border-radius:4px; padding:2px;' : 'padding:2px;'">
                                    <button type="button" title="Set as default tab"
                                            @click="setDefaultTab(tab.id)"
                                            :style="'flex:none; width:18px; padding:0; border:none; background:none; cursor:pointer; font-size:0.85rem; line-height:1; color:' + (tab.isDefault ? '#f59e0b' : '#d1d5db')"
                                            x-text="tab.isDefault ? '\u2605' : '\u2606'"></button>

                                    <input type="text"
                                           :value="tab.label"
                                           @focus="previewTab(tab.id)"
                                           @change="renameTab(tab.id, $event.target.value)"
                                           style="flex: 1; min-width: 0; padding: 0.25rem 0.375rem; border: 1px solid #d1d5db; border-radius: 0.25rem; font-size: 0.75rem;">

                                    <button type="button" title="Move up" @click="moveTab(tab.id, -1)" :disabled="i === 0"
                                            :style="'flex:none; width:14px; padding:0; border:none; background:none; cursor:pointer; font-size:0.6rem; color:' + (i === 0 ? '#e5e7eb' : '#6b7280')">&#9650;</button>
                                    <button type="button" title="Move down" @click="moveTab(tab.id, 1)" :disabled="i === tabsList.length - 1"
                                            :style="'flex:none; width:14px; padding:0; border:none; background:none; cursor:pointer; font-size:0.6rem; color:' + (i === tabsList.length - 1 ? '#e5e7eb' : '#6b7280')">&#9660;</button>
                                    <button type="button" title="Delete tab" @click="removeTab(tab.id)"
                                            style="flex:none; width:16px; padding:0; border:none; background:none; cursor:pointer; font-size:0.95rem; line-height:1; color:#ef4444;">&times;</button>
                                </div>
                            </template>

                            <button type="button" @click="addTab()"
                                    style="width: 100%; margin-top: 0.5rem; padding: 0.375rem; background: #3b82f6; color: white; border: none; border-radius: 0.375rem; cursor: pointer; font-size: 0.75rem;">+ Add tab</button>
                        </div>

                        <!-- CSS Classes (typeahead) -->
                        <div style="margin-bottom: 1rem;">
                            <label style="display: block; font-size: 0.75rem; font-weight: 600; color: #374151; margin-bottom: 0.5rem;">CSS Classes</label>
                            <div class="relative"
                                 x-data="cssClassTypeahead(window.wpsCssClasses || [])"
                                 @wps-classes-sync.window="value = $event.detail"
                                 style="position: relative;">
                                <input type="text"
                                       x-model="value"
                                       @input="onInput(); $dispatch('wps-classes-changed', value)"
                                       @keydown="onKeydown($event)"
                                       @focus="onInput"
                                       autocomplete="off"
                                       placeholder="e.g. col-6 page-content"
                                       style="width: 100%; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.75rem;">
                                <div x-show="isOpen" x-cloak
                                     style="position: absolute; z-index: 50; margin-top: 4px; width: 100%; background: white; border: 1px solid #e5e7eb; border-radius: 0.375rem; box-shadow: 0 4px 12px rgba(0,0,0,0.1); max-height: 240px; overflow: auto;">
                                    <template x-for="(suggestion, index) in suggestions" :key="suggestion">
                                        <div @click="selectSuggestion(suggestion); $dispatch('wps-classes-changed', value)"
                                             :style="'padding: 0.4rem 0.6rem; cursor: pointer; font-size: 0.75rem; ' + (selectedIndex === index ? 'background: #eff6ff; color: #1d4ed8;' : '')"
                                             x-text="suggestion"></div>
                                    </template>
                                </div>
                            </div>
                        </div>

                        <!-- Inline CSS Editor -->
                        <div style="margin-bottom: 1rem;">
                            <label style="display: block; font-size: 0.75rem; font-weight: 600; color: #374151; margin-bottom: 0.5rem;">Inline CSS</label>
                            <textarea x-model="currentElementStyle" @input="applyInlineStyle()" placeholder="color: #fff; font-size: 16px;" style="width: 100%; min-height: 80px; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-family: monospace; font-size: 0.75rem; resize: vertical;"></textarea>
                        </div>

                        <!-- Quick Properties (hidden when template has its own properties) -->
                        <div x-show="templateProperties.length === 0" style="margin-bottom: 1rem;">
                            <label style="display: block; font-size: 0.75rem; font-weight: 600; color: #374151; margin-bottom: 0.5rem;">Text Color</label>
                            <div style="display: flex; gap: 0.5rem;">
                                <input type="color" :value="(cssProperties['color'] || '#000000').startsWith('#') ? cssProperties['color'] : '#000000'" @input="updateCSSProperty('color', $event.target.value)" style="width: 3rem; height: 2rem; border: 1px solid #d1d5db; border-radius: 0.375rem;">
                                <input type="text" :value="cssProperties['color'] || ''" @input="updateCSSProperty('color', $event.target.value)" placeholder="#000000" style="flex: 1; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.75rem;">
                            </div>
                        </div>

                        <div x-show="templateProperties.length === 0" style="margin-bottom: 1rem;">
                            <label style="display: block; font-size: 0.75rem; font-weight: 600; color: #374151; margin-bottom: 0.5rem;">Background Color</label>
                            <div style="display: flex; gap: 0.5rem;">
                                <input type="color" :value="(cssProperties['background-color'] || '#ffffff').startsWith('#') ? cssProperties['background-color'] : '#ffffff'" @input="updateCSSProperty('background-color', $event.target.value)" style="width: 3rem; height: 2rem; border: 1px solid #d1d5db; border-radius: 0.375rem;">
                                <input type="text" :value="cssProperties['background-color'] || ''" @input="updateCSSProperty('background-color', $event.target.value)" placeholder="#ffffff" style="flex: 1; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.75rem;">
                            </div>
                        </div>

                        <!-- Template Properties -->
                        <template x-if="templateProperties.length > 0">
                            <div style="margin-bottom: 1rem; border-top: 1px solid #e5e7eb; padding-top: 1rem;">
                                <div style="font-size: 0.7rem; font-weight: 600; color: #6b7280; margin-bottom: 0.75rem;">BLOCK PROPERTIES</div>
                                <div x-show="templateProperties.some(p => p.responsive || p.type === 'layout')" style="font-size: 0.65rem; color: #6b7280; margin-bottom: 0.75rem; padding: 6px 8px; background: #eff6ff; border-radius: 4px;">
                                    Editing <strong x-text="(breakpoints.find(b => b.key === currentBreakpoint) || {}).label"></strong> — switch devices in the <strong>Content</strong> header above.
                                </div>
                                <template x-for="prop in templateProperties" :key="prop.name">
                                    <div style="margin-bottom: 0.75rem;">
                                        <label style="display: block; font-size: 0.75rem; font-weight: 600; color: #374151; margin-bottom: 0.25rem;"><span x-text="prop.label"></span><span x-show="prop.responsive || prop.type === 'layout'" style="font-weight: 400; color: #3b82f6;" x-text="' · ' + (breakpoints.find(b => b.key === currentBreakpoint) || {}).label"></span></label>
                                        <template x-if="prop.type === 'text'">
                                            <input type="text" :value="templatePropertyValues[prop.name]" @input="updateTemplateProperty(prop.name, $event.target.value)" style="width: 100%; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.75rem;">
                                        </template>
                                        <template x-if="prop.type === 'color'">
                                            <div style="display: flex; gap: 0.5rem;">
                                                <input type="color" :value="templatePropertyValues[prop.name] || '#000000'" @input="updateTemplateProperty(prop.name, $event.target.value)" style="width: 3rem; height: 2rem; border: 1px solid #d1d5db; border-radius: 0.375rem;">
                                                <input type="text" :value="templatePropertyValues[prop.name] || ''" @input="updateTemplateProperty(prop.name, $event.target.value)" style="flex: 1; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.75rem;">
                                            </div>
                                        </template>
                                        <template x-if="prop.type === 'range'">
                                            <div style="display: flex; align-items: center; gap: 0.5rem;">
                                                <input type="range" :value="templatePropertyValues[prop.name]" :min="prop.min" :max="prop.max" :step="prop.step || 1" @input="templatePropertyValues[prop.name] = $event.target.value; updateTemplateProperty(prop.name, $event.target.value)" style="flex: 1;">
                                                <input type="number" :value="templatePropertyValues[prop.name]" :min="prop.min" :max="prop.max" :step="prop.step || 1" @input="templatePropertyValues[prop.name] = $event.target.value; updateTemplateProperty(prop.name, $event.target.value)" style="width: 4rem; padding: 0.25rem 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.75rem; text-align: center;">
                                            </div>
                                        </template>
                                        <template x-if="prop.type === 'select'">
                                            <select :value="templatePropertyValues[prop.name]" @change="updateTemplateProperty(prop.name, $event.target.value)" style="width: 100%; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.75rem; background: white;">
                                                <template x-for="opt in (function(p) { let o = p.options; if (typeof o === 'string') { try { o = JSON.parse(o); } catch(e) { return []; } } if (typeof o === 'object' && !Array.isArray(o)) { return Object.entries(o).map(([v,l]) => ({value:v, label:l})); } return Array.isArray(o) ? o : []; })(prop)" :key="opt.value">
                                                    <option :value="opt.value" x-text="opt.label" :selected="opt.value === templatePropertyValues[prop.name]"></option>
                                                </template>
                                            </select>
                                        </template>
                                        <template x-if="prop.type === 'image'">
                                            <div>
                                                <div x-show="templatePropertyValues[prop.name]" style="margin-bottom: 0.5rem;">
                                                    <img :src="templatePropertyValues[prop.name]" style="width: 100%; height: 80px; object-fit: cover; border-radius: 0.375rem; border: 1px solid #d1d5db;">
                                                </div>
                                                <div style="display: flex; gap: 0.5rem;">
                                                    <button type="button" @click="window.openMediaLibraryPicker({ mediaType: 'image', onSelect: (m) => updateTemplateProperty(prop.name, m.url) })" style="flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 0.375rem; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.75rem; font-weight: 500; color: #374151; background: white; cursor: pointer;">
                                                        <i class="fas fa-photo-video"></i>
                                                        Choose from Library
                                                    </button>
                                                    <button type="button" x-show="templatePropertyValues[prop.name]" @click="updateTemplateProperty(prop.name, '')" style="padding: 0.5rem 0.75rem; border: 1px solid #fca5a5; border-radius: 0.375rem; font-size: 0.75rem; font-weight: 500; color: #b91c1c; background: white; cursor: pointer;">
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        </template>
                                        <template x-if="prop.type === 'layout'">
                                            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px;">
                                                <template x-for="preset in layoutPresets()" :key="preset.id">
                                                    <button type="button" @click="updateTemplateProperty(prop.name, preset.id)" :style="'padding: 8px 4px; font-size: 0.7rem; font-weight: 500; border: 1px solid ' + (templatePropertyValues[prop.name] === preset.id ? '#3b82f6' : '#d1d5db') + '; background: ' + (templatePropertyValues[prop.name] === preset.id ? '#eff6ff' : 'white') + '; color: #374151; border-radius: 6px; cursor: pointer;'" x-text="preset.label"></button>
                                                </template>
                                            </div>
                                        </template>
                                    </div>
                                </template>
                            </div>
                        </template>

                        <!-- Actions -->
                        <div style="border-top: 1px solid #e5e7eb; padding-top: 1rem;">
                            <button @click="if(selectedTemplate?.element) { selectedTemplate.element.scrollIntoView({behavior: 'smooth', block: 'center'}); selectedTemplate.element.style.outline = '2px solid #3b82f6'; setTimeout(() => selectedTemplate.element.style.outline = '', 2000); }" type="button" style="width: 100%; padding: 0.5rem; background: #3b82f6; color: white; border: none; border-radius: 0.375rem; cursor: pointer; font-size: 0.75rem; margin-bottom: 0.5rem;">Scroll to Block</button>
                            <button @click="if(selectedTemplate?.element) { if(confirm('Delete this block?')) { selectedTemplate.element.remove(); selectedTemplate = null; } }" type="button" style="width: 100%; padding: 0.5rem; background: #ef4444; color: white; border: none; border-radius: 0.375rem; cursor: pointer; font-size: 0.75rem;">Delete Block</button>
                        </div>
                    </div>
                </div>

                <!-- Tree Tab -->
                <div x-show="activeTab === 'tree'">
                    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem;">
                        <div style="font-size: 0.7rem; font-weight: 600; color: #6b7280;">DOCUMENT TREE</div>
                        <button @click="buildTree()" type="button" style="font-size: 0.7rem; color: #3b82f6; background: none; border: none; cursor: pointer;">Refresh</button>
                    </div>
                    <div x-show="treeNodes.length === 0" style="text-align: center; padding: 2rem 1rem; color: #9ca3af; font-size: 0.8rem;">
                        No content yet.
                    </div>
                    <div style="font-family: monospace; font-size: 0.72rem;">
                        <template x-for="node in treeNodes" :key="node.id">
                            <div @click="selectElementByTreeId(node.id)"
                                 :style="'padding: 3px 6px; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; border-radius: 4px; padding-left: ' + (6 + node.depth * 12) + 'px; ' + (activeTreeId === node.id ? 'background: #eff6ff; color: #1d4ed8;' : 'color: #374151;')"
                                 x-text="node.label"></div>
                        </template>
                    </div>
                </div>
            </div>
        `;

        return sidebar;
    }

    /**
     * Register a selector for auto-initialization
     * @param {string} selector - CSS selector for textarea(s)
     * @param {object} config - Editor configuration options
     */
    register(selector, config = {}) {
        this.registrations.set(selector, config);

        // Initialize immediately if DOM is ready
        if (document.readyState !== 'loading') {
            this.initSelector(selector, config);
        } else {
            document.addEventListener('DOMContentLoaded', () => {
                this.initSelector(selector, config);
            });
        }
    }

    /**
     * Initialize all textareas matching a selector
     * @param {string} selector - CSS selector for textarea(s)
     * @param {object} config - Editor configuration
     */
    async initSelector(selector, config = {}) {
        const textareas = document.querySelectorAll(selector);
        console.log(`🔍 Found ${textareas.length} textarea(s) for ${selector}`);

        const promises = [];
        textareas.forEach(textarea => {
            if (!textarea.id) {
                textarea.id = 'tinymce-' + Math.random().toString(36).substr(2, 9);
            }

            // Skip if already initialized
            if (this.instances.has(textarea.id)) {
                console.log(`⚠️  Editor already initialized for #${textarea.id}, skipping`);
                return;
            }

            promises.push(this.init(`#${textarea.id}`, config));
        });

        return Promise.all(promises);
    }

    /**
     * Initialize a single TinyMCE editor
     * @param {string} selector - CSS selector for the textarea
     * @param {object} config - Editor configuration
     * @returns {Promise<object>} TinyMCE editor instance
     */
    async init(selector, config = {}) {
        // Idempotency guard. init() is async and this.instances is only populated
        // AFTER tinymce.init() resolves, so a second trigger firing during that
        // await would otherwise pass initSelector()'s check and create a duplicate
        // editor. Guard synchronously here against an existing/in-flight instance.
        const id = selector.replace(/^#/, '');
        const existing = typeof tinymce !== 'undefined' && tinymce.get(id);
        if (existing) {
            this.instances.set(id, existing);
            return existing;
        }
        if (this.initializing.has(id)) {
            console.log(`⚠️  Editor init already in flight for #${id}, skipping`);
            return null;
        }
        this.initializing.add(id);

        const mergedConfig = {
            ...this.defaultConfig,
            ...config,
            selector: selector,
        };

        // The spread above (...config) lets a caller-supplied content_style overwrite the
        // default — and an empty string (the page editor passes content_style: "") would wipe
        // the default flex/grid reset entirely, leaving Tailwind's `body { display: grid }`
        // sticky-footer rule (injected via content_css) to push the last line to the bottom.
        // So ALWAYS start from the default reset, then append any caller styles. The page
        // chrome (centred email-width column, used by the Mailer editor) is only added when
        // the caller actually provided a content_style. It goes BEFORE the caller's styles so
        // the email wrapper's own container styling (Settings#editor_content_style) wins —
        // no grey backdrop or border here, so the editor matches the wrapper.
        const pageChrome = config.content_style ? `
                body {
                    max-width: 600px !important;
                    margin: 20px auto !important;
                    background: #fff !important;
                    min-height: calc(100vh - 40px) !important;
                }
            ` : '';
        mergedConfig.content_style =
            (this.defaultConfig.content_style || '') +
            (pageChrome ? '\n' + pageChrome : '') +
            (config.content_style ? '\n' + config.content_style : '');

        // Handle height configuration
        if (config.height) {
            mergedConfig.min_height = config.height;
        }

        // Always add variables support, even if starting with empty variables
        // This ensures the update function is registered for dynamic updates
        if (config.variables !== undefined) {
            this.addVariablesSupport(mergedConfig, config.variables || {});
        }

        try {
            const editors = await tinymce.init(mergedConfig);

            if (editors && editors.length > 0) {
                const editor = editors[0];
                this.instances.set(editor.id, editor);
                return editor;
            }
        } catch (error) {
            console.error('❌ Failed to initialize TinyMCE:', error);
            throw error;
        } finally {
            this.initializing.delete(id);
        }
    }

    /**
     * Add variables/merge fields support to TinyMCE
     * Creates a custom toolbar button for inserting variables
     */
    addVariablesSupport(config, variables) {
        const originalSetup = config.setup || (() => {});

        // Store variables in a mutable container so we can update them later
        const variablesContainer = { current: variables };

        // Prepend the Variables button to the toolbar BEFORE tinymce.init() reads the
        // config. TinyMCE shallow-copies the options object at editor creation, so
        // reassigning config.toolbar from inside setup() has no effect — the editor
        // already holds its own reference. (An array toolbar happened to survive that
        // because mutating element [0] mutates the shared array; a string does not.)
        // The toolbar is normally a string (single row, so toolbar_mode applies), but
        // callers may still pass an array of rows — handle both.
        if (Array.isArray(config.toolbar)) {
            if (config.toolbar.length === 0) {
                config.toolbar = ['variables'];
            } else if (!config.toolbar[0].includes('variables')) {
                config.toolbar[0] = 'variables | ' + config.toolbar[0];
            }
        } else if (typeof config.toolbar === 'string' && !config.toolbar.includes('variables')) {
            config.toolbar = 'variables | ' + config.toolbar;
        }

        config.setup = (editor) => {
            originalSetup(editor);

            // Add variables menu button
            editor.ui.registry.addMenuButton('variables', {
                text: 'Variables',
                icon: 'plus',
                fetch: (callback) => {
                    // Use the current variables from the container
                    const items = this.buildVariablesMenu(variablesContainer.current);
                    callback(items);
                }
            });

            // Register the update function in the global registry
            editor.on('init', () => {
                const updateFn = (newVariables) => {
                    console.log(`[TinyMCE] updateVariables called for ${editor.id}:`, newVariables);
                    variablesContainer.current = newVariables;
                    console.log('[TinyMCE] Variables updated successfully');
                };

                // Store in registry
                this.variablesRegistry.set(editor.id, updateFn);

                // Also add to editor instance for backwards compatibility
                editor.updateVariables = updateFn;

                console.log(`[TinyMCE] Editor ${editor.id} initialized with updateVariables method`);
            });

            // Clean up on remove
            editor.on('remove', () => {
                this.variablesRegistry.delete(editor.id);
            });
        };
    }

    /**
     * Build variables menu items
     * Supports both flat arrays and categorized objects
     */
    buildVariablesMenu(variables) {
        const items = [];

        // Check if variables is an object with categories
        if (typeof variables === 'object' && !Array.isArray(variables)) {
            // Categorized variables
            Object.entries(variables).forEach(([category, vars]) => {
                items.push({
                    type: 'nestedmenuitem',
                    text: category,
                    getSubmenuItems: () => {
                        return vars.map(v => this.createVariableMenuItem(v));
                    }
                });
            });
        } else if (Array.isArray(variables)) {
            // Flat array of variables
            items.push(...variables.map(v => this.createVariableMenuItem(v)));
        }

        // If no variables available, show a helpful message
        if (items.length === 0) {
            return [{
                type: 'menuitem',
                text: 'No variables available',
                enabled: false
            }];
        }

        return items;
    }

    /**
     * Create a menu item for a variable
     */
    createVariableMenuItem(variable) {
        return {
            type: 'menuitem',
            text: variable.label || variable.name,
            onAction: () => {
                console.log('[TinyMCE Variables] Attempting to insert variable:', variable);

                const activeEditor = tinymce.activeEditor;
                console.log('[TinyMCE Variables] Active editor:', activeEditor?.id);

                if (activeEditor) {
                    const token = variable.token || variable.value || `{{${variable.name}}}`;
                    console.log('[TinyMCE Variables] Inserting token:', token);

                    try {
                        // Get content before insertion
                        const contentBefore = activeEditor.getContent();
                        console.log('[TinyMCE Variables] Content before:', contentBefore);

                        // Focus editor
                        activeEditor.focus();

                        // TinyMCE treats {{}} as HTML tags and clears/hides them
                        // HTML-encode the curly braces so TinyMCE treats them as literal text
                        const encodedToken = token.replace(/\{/g, '&#123;').replace(/\}/g, '&#125;');

                        // Wrap the encoded variable in a div with CSS class
                        const wrappedToken = `<div class="variable-token">${encodedToken}</div>`;

                        // Check if editor is empty
                        if (!contentBefore || contentBefore.trim() === '' || contentBefore === '<p><br></p>' || contentBefore === '<p></p>') {
                            console.log('[TinyMCE Variables] Editor is empty, using setContent');
                            // Editor is empty, use setContent to initialize with the variable
                            activeEditor.setContent(`<p>${wrappedToken}&nbsp;</p>`);
                        } else {
                            // Editor has content, insert the wrapped variable
                            console.log('[TinyMCE Variables] Editor has content, using insertContent with wrapped token');
                            activeEditor.insertContent(wrappedToken);
                        }

                        // Get content after insertion
                        const contentAfter = activeEditor.getContent();
                        console.log('[TinyMCE Variables] Content after:', contentAfter);

                        // Force save to textarea
                        activeEditor.save();

                        // Verify it was actually inserted
                        if (contentAfter.includes(token)) {
                            console.log('[TinyMCE Variables] ✅ Variable inserted successfully');
                        } else {
                            console.error('[TinyMCE Variables] ⚠️ Variable was not inserted - content unchanged');
                        }
                    } catch (error) {
                        console.error('[TinyMCE Variables] ❌ Failed to insert variable:', error);
                    }
                } else {
                    console.error('[TinyMCE Variables] ❌ No active editor found');

                    // Try to find editor by ID as fallback
                    const editorId = 'mailer_template_body';
                    const editor = tinymce.get(editorId);
                    if (editor) {
                        console.log('[TinyMCE Variables] Found editor by ID, attempting insert...');
                        const token = variable.token || variable.value || `{{${variable.name}}}`;

                        const contentBefore = editor.getContent();
                        editor.focus();

                        // Check if editor is empty
                        if (!contentBefore || contentBefore.trim() === '' || contentBefore === '<p><br></p>' || contentBefore === '<p></p>') {
                            console.log('[TinyMCE Variables] Fallback: Editor is empty, using setContent');
                            editor.setContent(`<p>${token}</p>`);
                        } else {
                            editor.insertContent(token);
                        }

                        editor.save();

                        const contentAfter = editor.getContent();
                        console.log('[TinyMCE Variables] Content after fallback insert:', contentAfter);

                        if (contentAfter.includes(token)) {
                            console.log('[TinyMCE Variables] ✅ Variable inserted via fallback');
                        } else {
                            console.error('[TinyMCE Variables] ⚠️ Fallback insert failed - content unchanged');
                        }
                    }
                }
            }
        };
    }

    /**
     * Remove a specific editor instance
     * @param {string} id - Editor ID
     */
    remove(id) {
        const editor = tinymce.get(id);
        if (editor) {
            editor.remove();
        }
        this.instances.delete(id);
        console.log(`🗑️  Removed TinyMCE editor: ${id}`);
    }

    /**
     * Remove all editor instances
     */
    removeAll() {
        tinymce.remove();
        this.instances.clear();
        console.log('🗑️  Removed all TinyMCE editors');
    }

    /**
     * Get an editor instance
     * @param {string} id - Editor ID
     * @returns {object|null} TinyMCE editor instance
     */
    getInstance(id) {
        const editor = tinymce.get(id);

        // If editor exists and doesn't have updateVariables, try to add it from registry
        if (editor && !editor.updateVariables && this.variablesRegistry.has(id)) {
            editor.updateVariables = this.variablesRegistry.get(id);
            console.log(`[TinyMCE] Restored updateVariables method for ${id} from registry`);
        }

        return editor;
    }

    /**
     * Update variables for a specific editor
     * @param {string} id - Editor ID
     * @param {object} newVariables - New variables object
     */
    updateEditorVariables(id, newVariables) {
        const updateFn = this.variablesRegistry.get(id);
        if (updateFn) {
            updateFn(newVariables);
            return true;
        }

        // Fallback: try to get editor and call its method
        const editor = tinymce.get(id);
        if (editor && editor.updateVariables) {
            editor.updateVariables(newVariables);
            return true;
        }

        console.warn(`[TinyMCE] Cannot update variables for ${id}: no update function found`);
        return false;
    }

    /**
     * Setup Turbo compatibility for TinyMCE
     * Ensures editors are properly cleaned up and re-initialized on Turbo navigation
     */
    setupTurboCompatibility() {
        document.addEventListener('turbo:before-cache', () => {
            this.removeAll();
        });

        document.addEventListener('turbo:load', () => {
            // Re-initialize registered selectors
            this.registrations.forEach((config, selector) => {
                this.initSelector(selector, config);
            });
        });

        console.log('🔄 TinyMCE Turbo compatibility enabled');
    }

    /**
     * Setup accordion compatibility
     * Ensures editors work properly inside hidden accordion panels
     */
    setupAccordionCompatibility(selector, config = {}) {
        document.addEventListener('click', (e) => {
            const accordionButton = e.target.closest('[data-accordion-trigger]');
            if (accordionButton) {
                setTimeout(() => {
                    const panel = accordionButton.nextElementSibling;
                    if (panel) {
                        const textareas = panel.querySelectorAll(selector);
                        textareas.forEach(textarea => {
                            if (textarea.id && !this.instances.has(textarea.id)) {
                                this.init(`#${textarea.id}`, config);
                            }
                        });
                    }
                }, 100);
            }
        });
    }

    /**
     * Legacy compatibility method - alias for register()
     * @deprecated Use register() instead
     */
    setupAutoInit(selector, config = {}) {
        return this.register(selector, config);
    }
}

// Create singleton instance
const tinyMCELoader = new TinyMCELoader();

// Emit ready event when TinyMCE is loaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.dispatchEvent(new CustomEvent('tinymce:ready'));
    });
} else {
    setTimeout(() => {
        window.dispatchEvent(new CustomEvent('tinymce:ready'));
    }, 0);
}

export default tinyMCELoader;
